import React, { useCallback, useState } from "react";

import CustomButton from "components/common/Button";
import InlineLoader from "./InlineLoader";
import { listBillingDiscrepancies } from "services/billing";
import type { BsPaginated } from "services/billing";

import FilterSelect from "./FilterSelect";
import { useCursorList } from "./useCursorList";
import { CustomChip, TableComponent } from "./ui";
import { DISPUTE_STATUS_OPTIONS, formatDate, formatMoney, statusColor, titleCase } from "./types";

type ReconRow = Record<string, unknown>;

const columns = [
  { id: "type", label: "Type", render: (v: string) => titleCase(v) },
  {
    id: "disputeStatus",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "expectedAmountCents", label: "Expected", render: (v: number) => formatMoney(v) },
  { id: "actualAmountCents", label: "Actual", render: (v: number) => formatMoney(v) },
  { id: "differenceCents", label: "Difference", render: (v: number) => formatMoney(v) },
  { id: "stripeTransactionId", label: "Stripe txn", render: (v: string) => v || "—" },
  { id: "createdAt", label: "Created", render: (v: string) => formatDate(v) },
];

export default function ReconciliationTab() {
  // [PRD-3791] Dispute status filter.
  const [disputeStatus, setDisputeStatus] = useState("");
  const load = useCallback(
    (cursor?: string) =>
      listBillingDiscrepancies({ disputeStatus: disputeStatus || undefined, cursor, limit: 25 }) as Promise<
        BsPaginated<ReconRow>
      >,
    [disputeStatus]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore } = useCursorList<ReconRow>(load);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1 items-end" style={{ flexWrap: "wrap" }}>
        <FilterSelect
          name="reconciliation-status"
          label="Status"
          options={DISPUTE_STATUS_OPTIONS}
          value={disputeStatus}
          onChange={setDisputeStatus}
        />
      </div>
      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} disabled={loadingMore} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
